import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { tr } from "@/lib/i18n";
import { ArrowLeft, Plus, Check, Trash2, Undo, Target, Gift, Sparkles, Loader2, Timer, CalendarDays, Clock, Pencil, ChevronDown } from "lucide-react";
import PathBackdrop from "@/components/career/PathBackdrop";
import StepPostponeDialog from "@/components/career/StepPostponeDialog";
import StepEditDialog from "@/components/career/StepEditDialog";
import UkAgentGoalContent from "@/components/career/UkAgentGoalContent";
import GetMoreAuditionsGoalContent from "@/components/career/GetMoreAuditionsGoalContent";
import GetSeenGoalContent from "@/components/career/GetSeenGoalContent";
import MoreCastingsPLGoalContent from "@/components/career/MoreCastingsPLGoalContent";
import { statusOf, formatDate, formatCountdown } from "@/lib/crm";

const STEP_DOT = { red: "bg-rose-500", yellow: "bg-amber-400", green: "bg-emerald-500", gray: "bg-stone-600" };

function WhyIAmDoingThis({ goal, t, lang }) {
  const a = goal?.answers || {};
  const orderedRows = [
    { k: "q_creating",      label: t("goal.q_creating"),      val: a.q_creating,      pinned: true  },
    { k: "q1_success",      label: t("goal.q1"),              val: a.q1_success,      pinned: true  },
    { k: "q_possibilities", label: t("goal.q_possibilities"), val: a.q_possibilities, pinned: false },
    { k: "q5_impact",       label: t("goal.q5"),              val: a.q5_impact,       pinned: true  },
    { k: "q_have",          label: t("goal.q_have"),          val: a.q_have,          pinned: false },
    { k: "q_missing",       label: t("goal.q_missing"),       val: a.q_missing,       pinned: false },
    { k: "q_stops",         label: t("goal.q_stops"),         val: a.q_stops,         pinned: false },
    { k: "q_lose",          label: t("goal.q_lose"),          val: a.q_lose,          pinned: false },
    {
      k: "q4_hours_per_week",
      label: t("goal.q4"),
      val: (a.q4_hours_per_week !== null && a.q4_hours_per_week !== undefined && a.q4_hours_per_week !== "")
        ? `${a.q4_hours_per_week} ${t("goal.why_q4_unit")}`
        : "",
      pinned: false,
    },
  ];
  const pinnedRows = orderedRows.filter(r => r.pinned && (r.val || "").toString().trim());
  const restRows = orderedRows.filter(r => !r.pinned);
  const hasAnyRest = restRows.some(r => (r.val || "").toString().trim());
  const hasAny = pinnedRows.length > 0 || hasAnyRest;
  return (
    <div className="space-y-3">
      {/* Always-visible 3-answer summary — kierunek & sens planu */}
      {pinnedRows.length > 0 && (
        <div
          data-testid="goal-why-pinned"
          className="rounded-lg border border-amber-500/30 bg-amber-500/5 backdrop-blur-md p-5 space-y-4"
        >
          <div className="mono text-[11px] tracking-[0.3em] text-amber-500 uppercase flex items-center gap-2">
            <Sparkles size={12} /> {t("goal.why_title")}
          </div>
          {pinnedRows.map((r) => (
            <div key={r.k} data-testid={`goal-why-${r.k}`}>
              <div className="mono text-[10px] tracking-widest text-stone-400 uppercase">
                {r.label}
              </div>
              <div className="text-stone-100 mt-1 whitespace-pre-wrap leading-relaxed">
                {r.val}
              </div>
            </div>
          ))}
        </div>
      )}
      {/* Rest — inside existing accordion */}
      <details
        data-testid="goal-why-accordion"
        className="group rounded-lg border border-stone-800/60 bg-stone-900/40 backdrop-blur-md"
      >
        <summary
          className="list-none cursor-pointer flex items-center justify-between gap-3 p-5 select-none"
          data-testid="goal-why-toggle"
        >
          <div className="flex items-center gap-3 min-w-0">
            <Sparkles size={14} className="text-amber-500 shrink-0" />
            <div className="min-w-0">
              <div className="mono text-[11px] tracking-[0.3em] text-amber-500 uppercase">
                {t("goal.why_more_title")}
              </div>
              <div className="text-xs text-stone-400 mt-0.5 truncate">{t("goal.why_hint")}</div>
            </div>
          </div>
          <ChevronDown size={16} className="text-stone-400 transition-transform group-open:rotate-180 shrink-0" />
        </summary>
        <div className="border-t border-stone-800/60 p-5 pt-4 space-y-4">
          {!hasAny && (
            <div className="text-stone-500 text-sm italic">{t("goal.why_empty")}</div>
          )}
          {hasAnyRest && restRows.map((r) => {
            const val = r.val;
            if (!val || !val.toString().trim()) return null;
            return (
              <div key={r.k} data-testid={`goal-why-${r.k}`}>
                <div className="mono text-[10px] tracking-widest text-stone-500 uppercase">
                  {r.label}
                </div>
                <div className="text-stone-200 mt-1 whitespace-pre-wrap leading-relaxed">
                  {val}
                </div>
              </div>
            );
          })}
        </div>
      </details>
    </div>
  );
}

function StepRow({ s, t, lang, onToggle, onDelete, onEdit, onPostpone }) {
  const dot = s.done ? "gray"
    : s.due ? statusOf(s.due).color : "gray";
  const dotGlow = {
    red: "shadow-[0_0_8px_rgba(244,63,94,0.6)]",
    yellow: "shadow-[0_0_8px_rgba(251,191,36,0.6)]",
    green: "shadow-[0_0_8px_rgba(16,185,129,0.6)]",
    gray: "",
  };
  return (
    <li
      data-testid={`step-${s.step_id}`}
      className={`rounded-lg border border-stone-800/60 bg-stone-900/40 backdrop-blur-md p-6 md:p-10 transition-all hover:border-amber-500/50 hover:bg-stone-900/55 ${s.done ? "opacity-60" : ""}`}
    >
      <div className="flex flex-col lg:flex-row lg:items-center gap-6 lg:gap-12">
        {/* LEFT — subtle LED + checkbox + title + meta tags */}
        <div className="flex-1 flex items-start gap-4 min-w-0">
          <span
            className={`mt-3 inline-block w-2.5 h-2.5 rounded-full flex-shrink-0 ${STEP_DOT[dot]} ${dotGlow[dot]}`}
            aria-label={dot}
          />
          <button
            onClick={() => onToggle(s)}
            data-testid={`step-toggle-${s.step_id}`}
            className={`flex-shrink-0 mt-1 w-7 h-7 rounded-sm border flex items-center justify-center transition-colors ${
              s.done
                ? "bg-amber-500 border-amber-500 text-stone-950"
                : "border-stone-700 hover:border-amber-500"
            }`}
            aria-label={s.done ? t("step.mark_undone") : t("step.mark_done")}
          >
            {s.done ? <Check size={14} /> : null}
          </button>
          <div className="min-w-0 flex-1">
            <div className={`serif text-2xl md:text-3xl leading-tight ${s.done ? "line-through text-stone-500" : "text-stone-100"}`}>
              {s.name}
            </div>
            {s.source === "leo" && (
              <div className="mono text-[10px] uppercase tracking-widest text-amber-500 mt-2">
                ✦ {t("step.leo_banner")}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT — time + date (equal weight, horizontal) + action buttons */}
        <div className="flex flex-col items-start lg:items-end gap-3 lg:min-w-[300px]">
          {(s.est_duration || s.est_minutes) && (
            <div className="flex items-center gap-2 text-stone-300">
              <Timer size={20} className="text-amber-500" strokeWidth={2.5} />
              <span className="mono text-sm md:text-base uppercase tracking-widest">
                {s.est_duration || `${s.est_minutes} ${t("step.time_min")}`}
              </span>
            </div>
          )}
          {s.due ? (
            <div className="flex items-center gap-3">
              <CalendarDays size={26} className="text-amber-500" strokeWidth={2.5} />
              <div className="text-right">
                <div className="serif text-2xl md:text-3xl text-stone-100 leading-tight">
                  {formatDate(s.due, lang)}
                </div>
                <div className="mono text-xs text-stone-400 mt-0.5">
                  {formatCountdown(s.due, t)}
                </div>
              </div>
            </div>
          ) : null}
          {!s.done && (
            <div className="flex flex-wrap gap-2 mt-2 justify-start lg:justify-end">
              <button
                onClick={() => onPostpone(s)}
                data-testid={`step-postpone-${s.step_id}`}
                className="ghost-btn !py-3 !px-6 text-base md:text-lg border-2 hover:!border-amber-500/70 hover:!text-amber-300"
              >
                <Clock size={16} /> {t("ops.action.postpone")}
              </button>
              <button
                onClick={() => onEdit(s)}
                data-testid={`step-edit-${s.step_id}`}
                className="ghost-btn !py-3 !px-6 text-base md:text-lg border-2 hover:!border-amber-500/70 hover:!text-amber-300"
              >
                <Pencil size={16} /> {t("ops.action.edit")}
              </button>
            </div>
          )}
          <button
            onClick={() => onDelete(s)}
            data-testid={`step-delete-${s.step_id}`}
            className="ghost-btn !py-1.5 !px-2 text-xs hover:!text-rose-400 hover:!border-rose-500/50 mt-1"
          >
            <Trash2 size={12} />
          </button>
        </div>
      </div>
    </li>
  );
}

export default function GoalDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { lang } = useAuth();
  const t = (k) => tr(k, lang);

  const [goal, setGoal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newStepName, setNewStepName] = useState("");
  const [newStepMin, setNewStepMin] = useState("");
  const [newStepDue, setNewStepDue] = useState("");
  const [reward, setReward] = useState("");
  const [askHours, setAskHours] = useState(false);
  const [hoursInput, setHoursInput] = useState("");
  const [leoBusy, setLeoBusy] = useState(false);
  const [postponeStep, setPostponeStep] = useState(null);
  const [editStep, setEditStep] = useState(null);

  const load = async () => {
    try {
      const r = await api.get(`/career/goals/${id}`);
      setGoal(r.data);
      setReward(r.data.reward || "");
    } catch {
      toast.error("Not found"); nav("/career");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // Auto-generate the schedule for the templated Career Paths on first visit
  // if no plan has been produced yet. Reuses the SAME existing /leo-plan
  // endpoint used by the "Leo generuje plan" button below — no new pipeline.
  useEffect(() => {
    if (!goal) return;
    if (goal.template !== "uk_agent" && goal.template !== "get_more_auditions" && goal.template !== "more_castings_pl") return;
    if (goal.leo_plan_generated) return;
    if (leoBusy) return;
    const hpw = (goal.answers || {}).q4_hours_per_week;
    if (!hpw || hpw <= 0) return; // user must declare hours in the form first
    generatePlan(hpw);
    /* eslint-disable-next-line */
  }, [goal?.goal_id, goal?.template, goal?.leo_plan_generated]);

  if (loading || !goal) {
    return <div className="mono text-stone-500 text-sm">{t("dash.loading")}</div>;
  }

  // Null-safety hardening: the backend has, on rare occasions, returned
  // arrays containing nulls / partially-populated docs (e.g. legacy career
  // paths generated before schema stabilised, or a race between the LEO
  // plan writer and a step-delete). Filtering to plain objects here means
  // every downstream `.done` / `.name` / `.step_id` access is safe.
  const steps = (goal.steps || []).filter(
    (s) => s && typeof s === "object",
  );
  const total = steps.length;
  const doneCount = steps.filter((s) => s?.done).length;
  const progress = total ? Math.round((doneCount * 100) / total) : 0;
  const nextStep = steps.find((s) => !s?.done);

  const addStep = async () => {
    if (!newStepName.trim()) return;
    try {
      const r = await api.post(`/career/goals/${id}/steps`, {
        name: newStepName.trim(),
        est_minutes: newStepMin ? parseInt(newStepMin, 10) : null,
        due: newStepDue || null,
        done: false,
      });
      setGoal(r.data);
      setNewStepName(""); setNewStepMin(""); setNewStepDue("");
      toast.success(t("step.saved"));
    } catch { toast.error(t("step.save_fail")); }
  };

  const toggleStep = async (s) => {
    try {
      const r = await api.patch(`/career/goals/${id}/steps/${s.step_id}`,
        { done: !s.done });
      setGoal(r.data);
    } catch { toast.error(t("step.save_fail")); }
  };

  const delStep = async (s) => {
    if (!window.confirm(t("step.confirm_delete"))) return;
    try {
      const r = await api.delete(`/career/goals/${id}/steps/${s.step_id}`);
      setGoal(r.data);
    } catch { toast.error(t("step.save_fail")); }
  };

  const delGoal = async () => {
    if (!window.confirm(t("goal.confirm_delete"))) return;
    try {
      await api.delete(`/career/goals/${id}`);
      toast.success(t("goal.deleted"));
      nav("/career");
    } catch { toast.error(t("goal.save_fail")); }
  };

  const saveReward = async () => {
    try {
      const r = await api.patch(`/career/goals/${id}`, { reward });
      setGoal(r.data);
      toast.success(t("step.reward_saved"));
    } catch { toast.error(t("goal.save_fail")); }
  };

  const askLeo = () => {
    const hpw = (goal.answers || {}).q4_hours_per_week;
    if (!hpw || hpw <= 0) {
      setHoursInput("");
      setAskHours(true);
    } else {
      generatePlan(hpw);
    }
  };

  const generatePlan = async (hpw) => {
    setLeoBusy(true);
    setAskHours(false);
    try {
      const mode = (goal?.steps && goal.steps.length > 0) ? "update" : "first";
      const r = await api.post(
        `/career/goals/${id}/leo-plan?lang=${encodeURIComponent(lang)}`,
        { hours_per_week: hpw, mode }
      );
      setGoal(r.data);
      toast.success(t("step.leo_banner"));
    } catch (err) {
      toast.error(t("step.leo_fail"));
    } finally { setLeoBusy(false); }
  };

  const leoBannerText = (n) => {
    if (n === 1) return t("step.leo_banner_n_one");
    if (lang === "pl" && n >= 2 && n <= 4) return t("step.leo_banner_n_few").replace("{n}", n);
    return t("step.leo_banner_n_many").replace("{n}", n);
  };

  return (
    <div className="space-y-8 max-w-4xl" data-testid="goal-detail">
      <PathBackdrop />
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Link to="/career" className="ghost-btn text-xs" data-testid="goal-back">
          <ArrowLeft size={12} /> {t("goal.back")}
        </Link>
        <button
          onClick={delGoal}
          data-testid="goal-delete-btn"
          className="ghost-btn hover:!text-rose-400 hover:!border-rose-500/50"
        >
          <Trash2 size={14} /> {t("goal.delete")}
        </button>
      </div>

      <div>
        <div className="mono text-[11px] tracking-[0.4em] text-amber-500 uppercase flex items-center gap-2">
          <Target size={12} /> {t("path.title")}
        </div>
        <h1 className="serif text-5xl text-stone-100 mt-2">{goal.title || ""}</h1>
      </div>

      {/* Why I'm doing this — original workbook answers */}
      <WhyIAmDoingThis goal={goal} t={t} lang={lang} />

      {/* UK-Agent template content (visible only when goal.template === "uk_agent") */}
      {goal.template === "uk_agent" && (
        <UkAgentGoalContent goal={goal} lang={lang} onGoalUpdated={setGoal} />
      )}

      {/* Get More Auditions template content */}
      {goal.template === "get_more_auditions" && (
        <GetMoreAuditionsGoalContent goal={goal} lang={lang} onGoalUpdated={setGoal} />
      )}

      {/* BĄDŹ WIDOCZNY / GET SEEN template content */}
      {goal.template === "get_seen" && (
        <GetSeenGoalContent goal={goal} lang={lang} onGoalUpdated={setGoal} />
      )}

      {/* Zwiększ swoje szanse na castingi w Polsce — PL-only paid template */}
      {goal.template === "more_castings_pl" && (
        <MoreCastingsPLGoalContent goal={goal} onGoalUpdated={setGoal} />
      )}

      {/* Progress + Next Step (hero) */}
      <div className="paper p-5">
        {total > 0 ? (
          <>
            <div className="flex items-baseline justify-between gap-3 flex-wrap">
              <div className="label-cap">{t("step.progress")}</div>
              <div className="mono text-amber-500 uppercase tracking-widest">{progress}%</div>
            </div>
            <div className="mt-2 h-2 bg-stone-900 rounded overflow-hidden">
              <div className="h-full bg-amber-500 transition-all" style={{ width: `${progress}%` }} />
            </div>
            <div className="mt-5">
              <div className="mono text-[11px] tracking-[0.3em] text-stone-400 uppercase">
                {t("step.next_hero")}
              </div>
              <div className="serif text-2xl text-stone-100 mt-1" data-testid="next-step-hero">
                {nextStep ? (nextStep.name || t("goal.no_next_step")) : t("goal.no_next_step")}
              </div>
            </div>
          </>
        ) : (
          <div data-testid="goal-empty-cta">
            <div className="serif text-2xl text-stone-100">{t("goal.no_steps_yet_title")}</div>
            <p className="text-stone-400 text-sm mt-2 max-w-2xl">{t("goal.no_steps_yet_body")}</p>
            <div className="mt-4 flex items-center gap-2 flex-wrap">
              <button
                onClick={() => document.getElementById("new-step-name")?.focus()}
                className="ghost-btn"
                data-testid="goal-empty-plan-self"
              >
                {t("goal.plan_myself")}
              </button>
              <button
                className="pill-btn"
                data-testid="goal-empty-plan-leo"
                onClick={askLeo}
                disabled={leoBusy}
              >
                {leoBusy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                {leoBusy ? t("step.leo_generating_wait") : t("goal.plan_leo")}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Hours-per-week ask (if we don't know) */}
      {askHours && (
        <div className="paper p-5 border-amber-500/40 bg-amber-500/5" data-testid="leo-hours-ask">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles size={14} className="text-amber-500" />
            <div className="label-cap">{t("step.leo_ask_hours_title")}</div>
          </div>
          <div className="flex items-center gap-2 mt-2">
            <input
              autoFocus
              data-testid="leo-hours-input"
              type="number" min="0.5" step="0.5"
              value={hoursInput}
              onChange={(e) => setHoursInput(e.target.value)}
              placeholder={t("step.leo_ask_hours_ph")}
              className="script-input md:w-40"
            />
            <span className="text-stone-400 text-sm">{t("step.leo_ask_hours_unit")}</span>
            <button
              data-testid="leo-hours-submit"
              onClick={() => {
                const v = parseFloat(hoursInput);
                if (!isNaN(v) && v > 0) generatePlan(v);
              }}
              disabled={leoBusy || !parseFloat(hoursInput)}
              className="pill-btn"
            >
              {leoBusy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
              {t("step.leo_confirm")}
            </button>
            <button onClick={() => setAskHours(false)} className="ghost-btn text-xs">
              {t("action.cancel")}
            </button>
          </div>
        </div>
      )}

      {/* Leo plan banner */}
      {goal.leo_plan_generated && total > 0 && (
        <div className="flex items-center justify-between gap-3 flex-wrap" data-testid="leo-banner">
          <div className="flex items-center gap-2 text-sm">
            <Sparkles size={14} className="text-amber-500" />
            <span className="mono text-[10px] uppercase tracking-widest text-amber-500">
              {t("step.leo_banner")}
            </span>
            <span className="text-stone-400 italic">· {leoBannerText(total)}</span>
          </div>
          <button
            onClick={askLeo}
            data-testid="leo-update-btn"
            disabled={leoBusy}
            className="ghost-btn text-xs"
          >
            {leoBusy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
            {t("step.leo_update")}
          </button>
        </div>
      )}

      {/* All steps */}
      <section>
        <div className="label-cap mb-3">{t("step.list_title")}</div>
        <ul className="space-y-2">
          {steps.map((s, idx) => (
            <StepRow
              key={s.step_id || `step-${idx}`}
              s={s}
              t={t}
              lang={lang}
              onToggle={toggleStep}
              onDelete={delStep}
              onEdit={setEditStep}
              onPostpone={setPostponeStep}
            />
          ))}
        </ul>

        <div className="paper p-3 mt-3 flex flex-col md:flex-row gap-2 items-stretch">
          <input
            id="new-step-name"
            data-testid="new-step-name"
            value={newStepName}
            onChange={(e) => setNewStepName(e.target.value)}
            placeholder={t("step.name_ph")}
            className="script-input flex-1"
          />
          <input
            data-testid="new-step-min"
            type="number" min="0" step="5"
            value={newStepMin}
            onChange={(e) => setNewStepMin(e.target.value)}
            placeholder={t("step.time_min")}
            className="script-input md:w-24"
          />
          <input
            data-testid="new-step-due"
            type="date"
            value={newStepDue}
            onChange={(e) => setNewStepDue(e.target.value)}
            className="script-input md:w-40"
          />
          <button
            onClick={addStep}
            data-testid="new-step-add"
            className="pill-btn"
          >
            <Plus size={14} /> {t("step.add")}
          </button>
        </div>
      </section>

      {/* Monthly reward */}
      <section className="paper p-5">
        <div className="flex items-center gap-2 mb-2">
          <Gift size={14} className="text-amber-500" />
          <div className="label-cap">{t("step.reward_title")}</div>
        </div>
        <textarea
          data-testid="goal-reward"
          value={reward}
          onChange={(e) => setReward(e.target.value)}
          placeholder={t("step.reward_ph")}
          rows={2}
          className="script-input w-full resize-none"
        />
        <div className="mt-3 flex justify-end">
          <button onClick={saveReward} data-testid="goal-reward-save" className="ghost-btn">
            {t("step.reward_save")}
          </button>
        </div>
      </section>

      <StepPostponeDialog
        goalId={id}
        step={postponeStep}
        onClose={() => setPostponeStep(null)}
        onDone={(g) => g && setGoal(g)}
      />
      <StepEditDialog
        goalId={id}
        step={editStep}
        onClose={() => setEditStep(null)}
        onDone={(g) => g && setGoal(g)}
      />
    </div>
  );
}
